import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "../api/client";
import {
  useDropdownLists,
  invalidateDropdownCache,
} from "../hooks/useDropdownOptions";
import type { DropdownListItem } from "../hooks/useDropdownOptions";
import i18next from "i18next";

interface ComboboxSelectProps {
  listCode: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export default function ComboboxSelect({
  listCode,
  value,
  onChange,
  placeholder = "",
  className = "input",
  disabled = false,
}: ComboboxSelectProps) {
  const { lists } = useDropdownLists();
  const lang = i18next.language || "en-GB";
  const isEs = lang === "es";

  const list = lists.find((l: DropdownListItem) => l.code === listCode);
  const options = (list?.options ?? [])
    .filter((o) => o.is_active)
    .map((o) => ({
      value: o.code,
      label: isEs && o.label_es ? o.label_es : o.label,
    }));

  const [inputValue, setInputValue] = useState(value);
  const [open, setOpen] = useState(false);
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const filtered = options.filter(
    (o) =>
      !inputValue ||
      o.label.toLowerCase().includes(inputValue.toLowerCase()),
  );

  const exactMatch = options.some(
    (o) => o.label.toLowerCase() === inputValue.toLowerCase(),
  );

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
      setOpen(false);
      setConfirmCreate(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const selectOption = (val: string) => {
    const opt = options.find((o) => o.value === val);
    setInputValue(opt?.label ?? val);
    onChange(val);
    setOpen(false);
    setConfirmCreate(false);
  };

  const handleCreate = async () => {
    if (!list || !inputValue.trim()) return;
    setCreating(true);
    try {
      const code = inputValue
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "");
      await api.post(`/api/dropdowns/${list.id}/options`, {
        code,
        label: inputValue.trim(),
      });
      invalidateDropdownCache();
      onChange(code);
      setOpen(false);
      setConfirmCreate(false);
    } catch {
      onChange(inputValue.trim());
      setOpen(false);
      setConfirmCreate(false);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          onChange(e.target.value);
          setOpen(true);
          setConfirmCreate(false);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
      />

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {filtered.length > 0 ? (
            filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => selectOption(o.value)}
                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                {o.label}
              </button>
            ))
          ) : inputValue.trim() && !exactMatch ? (
            <div className="px-3 py-2">
              {!confirmCreate ? (
                <div className="text-sm text-gray-500">
                  No match found.{" "}
                  <button
                    type="button"
                    onClick={() => setConfirmCreate(true)}
                    className="font-medium text-pme-blue hover:underline"
                  >
                    Create &lsquo;{inputValue.trim()}&rsquo;?
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-gray-700">
                    Add &lsquo;{inputValue.trim()}&rsquo; to the job titles list?
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={creating}
                      onClick={() => void handleCreate()}
                      className="rounded-lg bg-pme-blue px-3 py-1 text-xs font-semibold text-white disabled:opacity-60"
                    >
                      {creating ? "Adding..." : "Yes"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmCreate(false);
                        setOpen(false);
                      }}
                      className="rounded-lg border border-gray-300 px-3 py-1 text-xs"
                    >
                      No
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {inputValue.trim() && !exactMatch && filtered.length > 0 && (
            <div className="border-t border-gray-100 px-3 py-2">
              {!confirmCreate ? (
                <div className="text-sm text-gray-500">
                  Or{" "}
                  <button
                    type="button"
                    onClick={() => setConfirmCreate(true)}
                    className="font-medium text-pme-blue hover:underline"
                  >
                    create &lsquo;{inputValue.trim()}&rsquo;
                  </button>{" "}
                  as a new option
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-gray-700">
                    Add &lsquo;{inputValue.trim()}&rsquo; to the job titles list?
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={creating}
                      onClick={() => void handleCreate()}
                      className="rounded-lg bg-pme-blue px-3 py-1 text-xs font-semibold text-white disabled:opacity-60"
                    >
                      {creating ? "Adding..." : "Yes"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmCreate(false);
                        setOpen(false);
                      }}
                      className="rounded-lg border border-gray-300 px-3 py-1 text-xs"
                    >
                      No
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
